import React from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import { IconButton } from '@mui/material';
import { useTranslation } from 'next-i18next';

import AppTitle from '@/components/common/title';
import { routes } from '@/infrastructure/const/routes';
import LazyImage from '@/components/common/lazy-image';
import { Reciter } from '@/infrastructure/class/reciter';
import { scrollToElement } from '@/utils/helpers/scroll-to-element';
import { usePlayer } from '@/infrastructure/context/player-context';
import { groupArrayByField } from '@/utils/helpers/group-array-items';
import { EMPTY_IMAGE_PATH } from '@/infrastructure/const/empty-image-path';
import { ReciterAudioSurah } from '@/infrastructure/class/reciter-audio-surah';
import { AudioSurahsFilterType } from '@/infrastructure/enum/audio-surahs-filter-type';
import {
  CircleOutlined,
  CloseRounded,
  RadioButtonCheckedOutlined,
  LaunchOutlined,
} from '@mui/icons-material';
import s from './styles.module.scss';

interface IReciterSelectorFormProps {
  items: ReciterAudioSurah[];
  onClose?: () => void;
}

const ReciterSelectorForm: React.FC<IReciterSelectorFormProps> = (props) => {
  const { items, onClose = () => {} } = props;
  const { player, setTracks } = usePlayer();

  const { t: tButtons } = useTranslation('buttons');

  const groupedItems = groupArrayByField(items, 'title');

  const filters = [
    { name: tButtons('all'), type: AudioSurahsFilterType.ALL },

    {
      name: tButtons('mostPopular'),
      type: AudioSurahsFilterType.MOST_POPULAR,
    },
    {
      name: tButtons('myFavorite'),
      type: AudioSurahsFilterType.FAVORITE,
    },
  ];

  const isCurrentSurah = (reciterAudioSurah: ReciterAudioSurah) =>
    reciterAudioSurah.audioSurah.slug === player.currentTrack?.slug;

  const iconBtnClassNames = (reciterAudioSurah: ReciterAudioSurah) =>
    classNames(s['check-btn'], {
      [s['active']]: isCurrentSurah(reciterAudioSurah),
    });

  const getReciterImage = (reciter: Reciter) =>
    !!reciter.image ? reciter.image.mediumUrl : EMPTY_IMAGE_PATH;

  const onWordClick = (wordId: string) => {
    const el = document.getElementById(wordId);
    if (el) scrollToElement(el);
  };

  const setTrack = (reciterAudioSurah: ReciterAudioSurah) => {
    const tracks = items.map((item) => item.audioSurah);
    setTracks(tracks, reciterAudioSurah.audioSurah);
  };

  return (
    <div className={s['form']}>
      <div className={s['header']}>
        <AppTitle title={tButtons('reciters')} />
        <IconButton onClick={onClose}>
          <CloseRounded />
        </IconButton>
      </div>
      <div className={s['body']}>
        <div className={s['words']}>
          {groupedItems.map((groupedItem) => (
            <button
              className={s['word']}
              key={groupedItem[0].title[0]}
              onClick={() => onWordClick(`word_${groupedItem[0].title[0]}`)}
            >
              {groupedItem[0].title[0]}
            </button>
          ))}
        </div>
        {/* TODO need to finish filter */}
        {/* <div className={s['filters']}>
          {filters.map((filter) => (
            <button className={s['filter']} key={filter.name}>
              {filter.name}
            </button>
          ))}
        </div> */}

        <ul className={s['reciters']}>
          {groupedItems.map((groupedItem) => (
            <li className={s['word']} key={groupedItem[0].title[0]}>
              <p id={`word_${groupedItem[0].title[0]}`}>
                {groupedItem[0].title[0]}
              </p>
              <ul>
                {groupedItem.map((item) => (
                  <li
                    className={s['reciter-item']}
                    key={item.title}
                    onClick={() => setTrack(item)}
                  >
                    <IconButton className={iconBtnClassNames(item)}>
                      {isCurrentSurah(item) ? (
                        <RadioButtonCheckedOutlined />
                      ) : (
                        <CircleOutlined />
                      )}
                    </IconButton>
                    <div>
                      <div className={s['image']}>
                        <LazyImage
                          className={s['image']}
                          alt={item.reciter.slug}
                          src={getReciterImage(item.reciter)}
                          fill
                        />
                      </div>
                    </div>
                    <p className={s['title']}>
                      {item.reciter.titleEn}
                      <br />
                      {item.reciter.titleAr}
                    </p>
                    <Link
                      href={`${routes.reciters}/${item.reciter.slug}`}
                      prefetch={false}
                      className={s['link']}
                    >
                      <IconButton>
                        <LaunchOutlined />
                      </IconButton>
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ReciterSelectorForm;
